import { z } from 'zod';
import { cents, entityId, isoDateTime } from './primitives.js';

/**
 * ANUNCIO
 *
 * O anuncio tem um nucleo estavel (o que a busca, o ranking e o match
 * precisam indexar) e um saco de `attributes` dinamicos alimentado pelo
 * FormSchema. Campos novos criados no painel caem em `attributes` sem
 * migracao de banco; campos promovidos a coluna forte ganham `mapsTo`.
 */

export const locationPrivacy = z.enum(['exact', 'neighborhood', 'region', 'city', 'hidden']);
export type LocationPrivacy = z.infer<typeof locationPrivacy>;

export const listingStatus = z.enum([
  'draft',
  'pending_review',
  'changes_requested',
  'active',
  'paused',
  'expired',
  'sold',
  'suspended',
  'removed',
]);
export type ListingStatus = z.infer<typeof listingStatus>;

export const media = z.object({
  id: entityId,
  kind: z.enum(['image', 'video', 'document', 'floorplan']),
  url: z.string(),
  thumbnailUrl: z.string().nullable().default(null),
  caption: z.string().nullable().default(null),
  order: z.number().int().default(0),
  /** Hash perceptual, usado na deteccao de fotos repetidas (Cap. 23.1). */
  perceptualHash: z.string().nullable().default(null),
});

export type Media = z.infer<typeof media>;

/**
 * Localizacao ja resolvida para o publico. O backend aplica a privacidade
 * ANTES de serializar: se o anunciante escolheu "somente cidade", os campos
 * de logradouro e coordenada chegam nulos no cliente. Nunca confiar em
 * esconder no front (Cap. 7.2).
 */
export const publicLocation = z.object({
  state: z.string().nullable().default(null),
  city: z.string().nullable().default(null),
  cityId: entityId.nullable().default(null),
  neighborhood: z.string().nullable().default(null),
  region: z.string().nullable().default(null),
  subdivision: z.string().nullable().default(null),
  street: z.string().nullable().default(null),
  number: z.string().nullable().default(null),
  complement: z.string().nullable().default(null),
  zipCode: z.string().nullable().default(null),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  /** Raio de ofuscacao aplicado ao ponto, em metros. 0 = coordenada exata. */
  blurRadiusMeters: z.number().int().min(0).default(0),
  privacy: locationPrivacy,
});

export type PublicLocation = z.infer<typeof publicLocation>;

/**
 * O anunciante como o PÚBLICO o vê.
 *
 * NÃO EXISTE CAMPO DE CONTATO AQUI, e a ausência é a regra — não um descuido a
 * ser corrigido depois.
 *
 * O contrato declarava `phone` e `whatsapp`, "presentes apenas quando o
 * roteamento de contato permite". Isso transformava o portal numa base gratuita
 * de captação: corretores e imobiliárias concorrentes varreriam os anúncios e
 * montariam a lista de proprietários — que é o ativo do SOLOTE, entregue de
 * graça a quem compete com ele.
 *
 * A conexão passa a começar dentro da plataforma: o comprador se identifica, o
 * SOLOTE registra, e o PROPRIETÁRIO recebe o contato e liga. A promessa de
 * negociar diretamente continua de pé; muda de que lado a ligação começa.
 *
 * Os campos foram REMOVIDOS do tipo, e não deixados nulos, de propósito: campo
 * declarado é campo que alguém preenche. Sem ele, o caminho antigo não compila
 * — e proteção que o compilador garante vale mais que proteção que alguém
 * precisa lembrar.
 */
export const advertiserSummary = z.object({
  id: entityId,
  displayName: z.string(),
  kind: z.enum(['owner', 'broker', 'agency', 'developer', 'company', 'organization', 'other']),
  avatarUrl: z.string().nullable().default(null),
  creci: z.string().nullable().default(null),
  verified: z.boolean().default(false),
  memberSince: isoDateTime.nullable().default(null),
});

export type AdvertiserSummary = z.infer<typeof advertiserSummary>;

export const listing = z.object({
  id: entityId,
  /** Codigo publico unico, ex.: STL-000184 (Cap. 2.2). */
  code: z.string(),
  /** Codigo interno do anunciante, opcional (Cap. 6.1). */
  advertiserReference: z.string().nullable().default(null),
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable().default(null),
  status: listingStatus,

  // --- nucleo indexado ---
  priceCents: cents.nullable().default(null),
  pricePerSquareMeterCents: cents.nullable().default(null),
  negotiable: z.boolean().default(false),
  totalAreaM2: z.number().nullable().default(null),
  frontageM: z.number().nullable().default(null),
  aptitudes: z.array(z.string()).default([]),
  topography: z.string().nullable().default(null),
  infrastructure: z.array(z.string()).default([]),
  documentation: z.array(z.string()).default([]),
  features: z.array(z.string()).default([]),

  /** Tudo que veio de campos criados no painel. */
  attributes: z.record(z.string(), z.unknown()).default({}),

  location: publicLocation,
  media: z.array(media).default([]),
  advertiser: advertiserSummary.nullable().default(null),

  // --- vitrine ---
  featured: z.boolean().default(false),
  /** Score de ordenacao ja calculado pelo backend (plano + recencia + qualidade). */
  rankingScore: z.number().default(0),

  publishedAt: isoDateTime.nullable().default(null),
  updatedAt: isoDateTime,
  expiresAt: isoDateTime.nullable().default(null),
  /** Dias ativos — insumo da regra dos 90 dias (Cap. 14). */
  activeDays: z.number().int().min(0).default(0),

  /**
   * Motivo da ultima decisao de moderacao.
   *
   * So vem preenchido para o DONO do anuncio, em `/me/listings`. Na vitrine
   * publica e sempre nulo: o texto costuma citar suspeita de fraude, e expo-lo
   * a qualquer visitante seria acusacao publica sem contraditorio.
   */
  moderationNote: z.string().nullable().default(null),

  stats: z
    .object({
      views: z.number().int().min(0).default(0),
      contacts: z.number().int().min(0).default(0),
      favorites: z.number().int().min(0).default(0),
    })
    .default({}),
});

export type Listing = z.infer<typeof listing>;

/** Versao enxuta usada em grades e carrosseis. */
export const listingCard = listing.pick({
  id: true,
  code: true,
  slug: true,
  title: true,
  priceCents: true,
  pricePerSquareMeterCents: true,
  totalAreaM2: true,
  aptitudes: true,
  location: true,
  featured: true,
  publishedAt: true,
}).extend({
  coverImageUrl: z.string().nullable().default(null),
  /** Selos calculados pelo backend: "Novo", "Abaixo da media", "Com matricula". */
  badges: z.array(z.object({ key: z.string(), label: z.string(), tone: z.string().default('neutral') })).default([]),
});

export type ListingCard = z.infer<typeof listingCard>;
